import { ConfigurableColumns } from '@/components/ConfigurableColumns';
import { CopiableIdDataGridField } from '@/components/CopiableIdField';
import {
  GenericDateFunctionField,
  GenericRelativeDateDisplay,
} from '@/components/GenericDateFunctionField';
import {
  BooleanField,
  EmailField,
  FunctionField,
  NumberField,
} from 'react-admin';
import {
  renderAccountStatusSummary,
  renderBillingIssuesFlag,
  renderCustomerSummary,
  renderFreeSubscriptionSummary,
  renderPaidSubscriptionSummary,
  renderPendingCancellationSummary,
  renderReactivatedFlag,
  renderServiceAvailability,
  renderTrialSummary,
} from './AccountInfo';
import { Chip } from '@mui/material';

export const columns: ConfigurableColumns[] = [
  {
    label: 'Creation',
    source: 'createdAt',
    sortable: true,
    GridComponent: GenericDateFunctionField,
    exportFn: record => record.createdAt?.toString(),
  },
  {
    label: 'Timezone',
    source: 'timezoneInfo.offset',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) =>
        record.timezoneInfo ? (
          <Chip
            label={`${record.timezoneInfo.name} (${record.timezoneInfo.gmt})`}
          />
        ) : null,
    },
    omit: true,
  },
  {
    label: 'First App Version',
    source: 'firstKnownAppVersion',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) =>
        record.timezoneInfo ? (
          <Chip label={`${record.firstKnownAppVersion}`} />
        ) : null,
    },
    omit: true,
  },
  {
    label: 'Current App Version',
    source: 'lastKnownAppVersion',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) =>
        record.lastKnownAppVersion ? (
          <Chip label={`${record.lastKnownAppVersion}`} />
        ) : null,
    },
    omit: true,
  },
  {
    label: 'Therapist Id',
    source: 'id',
    sortable: true,
    exportFn: record => record.id,
    GridComponent: CopiableIdDataGridField,
    gridComponentProps: {
      targetNavigationResource: 'therapists',
      referenceField: '_id',
      copyLink: true,
    },
  },
  {
    label: 'Title',
    source: 'title',
  },
  {
    label: 'First Name',
    source: 'firstName',
  },
  {
    label: 'Last Name',
    source: 'lastName',
  },
  {
    label: 'Email',
    source: 'email',
    GridComponent: EmailField,
  },
  {
    label: 'Phone Number',
    source: 'phoneNumber',
  },
  {
    label: 'Signup Completion',
    source: 'signupCompletedAt',
    GridComponent: GenericDateFunctionField,
    omit: true,
  },
  {
    label: 'Last Opened At',
    source: 'lastOpenedAt',
    GridComponent: GenericDateFunctionField,
  },
  {
    label: 'Test Account',
    source: 'isHiddenTestAccount',
    GridComponent: BooleanField,
    omit: true,
  },
  {
    label: 'Service Availability',
    source: 'accountInfo.isActive',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: renderServiceAvailability,
    },
  },
  {
    label: 'Account Status',
    source: 'accountInfo.status',
    sortable: true,
    GridComponent: FunctionField,
    gridComponentProps: {
      render: renderAccountStatusSummary,
    },
  },
  {
    label: 'Authorization Provider',
    source: 'providerData.iss',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) =>
        record.providerData?.iss === 'https://appleid.apple.com' ? (
          <Chip label="Apple" color="success" />
        ) : (
          <Chip label="Auth0" color="default" />
        ),
    },
  },
  {
    label: 'Apple SSO: Connected to Legacy Account',
    source: 'appleIntegrationConnected',
    sortable: false,
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) => {
        if (
          record.providerData?.iss === 'https://appleid.apple.com'
        ) {
          return null;
        }

        return record.appleIntegrationConnected ? (
          <Chip label="Connected" color="success" />
        ) : (
          <Chip label="Pending" color="default" />
        );
      },
    },
  },
  {
    label: 'Trial Status',
    source: 'accountInfo.trialStatus',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: renderTrialSummary,
    },
    omit: true,
  },
  {
    label: 'Free Subscription',
    source: 'accountInfo.freeSubscriptionExpirationDate',
    GridComponent: FunctionField,
    omit: true,
    gridComponentProps: {
      render: renderFreeSubscriptionSummary,
    },
  },
  {
    label: 'Paying Customer',
    source: 'accountInfo.isCustomer',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: renderCustomerSummary,
    },
    omit: true,
  },
  {
    label: 'PAID Subscription Active:',
    source: 'accountInfo.isSubscriptionActive',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: renderPaidSubscriptionSummary,
    },
    omit: true,
  },
  {
    label: 'Subscription Start',
    source: 'billing.currentSubscription.current_period_starts_at',
    sortable: true,
    GridComponent: GenericDateFunctionField,
    exportFn: doc => {
      const subscriptionStart =
        doc.billing?.currentSubscription?.current_period_starts_at;

      if (!subscriptionStart) {
        return '';
      }

      return new Date(subscriptionStart).toISOString();
    },
    omit: true,
  },
  {
    label: 'Subscription Expiration',
    source: 'billing.currentSubscription.current_period_ends_at',
    GridComponent: GenericDateFunctionField,
    exportFn: doc => {
      const subscriptionStart =
        doc.billing?.currentSubscription?.current_period_ends_at;

      if (!subscriptionStart) {
        return '';
      }

      return new Date(subscriptionStart).toISOString();
    },
    omit: true,
  },
  {
    label: 'FIRST Subscription Start',
    source: 'accountInfo.firstSubscribedAt',
    omit: true,
    GridComponent: GenericDateFunctionField,
    exportFn: doc => {
      const subscriptionStart = doc.accountInfo?.firstSubscribedAt;

      if (!subscriptionStart) {
        return '';
      }

      return new Date(subscriptionStart).toISOString();
    },
  },
  {
    label: 'Pending Cancellation?',
    source: 'accountInfo.isPendingCancellation',
    omit: true,
    GridComponent: FunctionField,
    gridComponentProps: {
      render: renderPendingCancellationSummary,
    },
  },
  {
    label: 'Reactivated?',
    source: 'accountInfo.isReactivated',
    omit: true,
    GridComponent: FunctionField,
    gridComponentProps: {
      render: renderReactivatedFlag,
    },
  },
  {
    label: 'Billing Issues?',
    source: 'accountInfo.hasBillingIssues',
    omit: true,
    GridComponent: FunctionField,
    gridComponentProps: {
      render: renderBillingIssuesFlag,
    },
  },
  {
    label: 'Trial Notes',
    source: 'freeTrial.count',
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Trial Usage',
    source: 'freeTrial.usage',
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Trial Balance',
    source: 'freeTrial.balance',
    GridComponent: NumberField,
    sortable: true,
  },
  {
    label: 'Clients/week',
    source: 'onboardingResponses_patientsPerWeek',
    // GridComponent: ChipField,
    // gridComponentProps: {
    //   source: "onboardingResponses_patientsPerWeek",
    // },
  },
  {
    label: 'Practice Type',
    source: 'onboardingResponses_practiceType',
    // GridComponent: ChipField,
    // gridComponentProps: {
    //   source: "onboardingResponses_practiceType",
    // },
  },
  {
    label: 'Clients (active)',
    source: 'patients.active.count',
    GridComponent: NumberField,
  },
  {
    label: 'Last Client Created At',
    source: 'patients.lastCreatedAt',
    GridComponent: GenericDateFunctionField,
    omit: true,
  },
  {
    label: 'Clients (connected)',
    source: 'patients.connected.count',
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Clients (archived)',
    source: 'patients.archived.count',
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Clients (deleted)',
    source: 'patients.deleted.count',
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Session Notes (active)',
    source: 'sessionNotes.active.count',
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Session Notes (committed)',
    source: 'sessionNotes.committed.count',
    GridComponent: NumberField,
    omit: false,
    sortable: true,
  },
  {
    label: 'Session Notes (sample)',
    source: 'sessionNotes.sample.count',
    GridComponent: NumberField,
    omit: false,
    sortable: true,
  },
  {
    label: 'Onboarding Completion',
    source: 'onboardingCompletedAt',
    GridComponent: FunctionField,
    sortable: true,
    gridComponentProps: {
      render: (record: any) => {
        if (!record.onboardingCompletedAt) {
          return null;
        }

        return (
          <GenericRelativeDateDisplay
            date={record.onboardingCompletedAt as any}
            format="MM/dd/yyyy hh:mm:ss a"
          />
        );
      },
    },
    exportFn: (record: any) => {
      if (!record.onboardingCompletedAt) {
        return null;
      }

      return record.onboardingCompletedAt;
    },
  },
  {
    label: 'Notifications',
    source: 'notificationsEnabled',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) =>
        record.notificationsEnabled ? (
          <Chip label="Enabled" color="success" />
        ) : (
          <Chip label="Disabled" color="error" />
        ),
    },
    // omit: true,
  },
  {
    label: 'Calendar Sync',
    source: 'calendarIntegration.resourceId',
    GridComponent: FunctionField,
    gridComponentProps: {
      render: (record: any) =>
        record.calendarIntegration?.resourceId ? (
          <Chip label="Connected" color="success" />
        ) : (
          <Chip label="Not Connected" color="error" />
        ),
    },
    exportFn: (record: any) =>
      !record.calendarIntegration?.resourceId
        ? 'Not Connected'
        : 'Connected',
    // omit: true,
  },
  {
    label: 'Last Session Note',
    sortable: true,
    source: 'sessionNotes.lastCreatedAt',
    GridComponent: GenericDateFunctionField,
  },
  {
    label: 'Session Notes (deleted)',
    source: 'sessionNotes.deleted.count',
    sortable: true,
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Session Notes (total)',
    sortable: true,
    source: 'sessionNotes.total',
    GridComponent: NumberField,
  },
  {
    label: 'Dictation (active)',
    source: 'sessionNotes.dictation.active.count',
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Last Dictation',
    source: 'sessionNotes.dictation.lastCreatedAt',
    GridComponent: GenericDateFunctionField,
    omit: true,
  },
  {
    label: 'Dictation (deleted)',
    source: 'sessionNotes.dictation.deleted.count',
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Live Notes (active)',
    source: 'sessionNotes.live.active.count',
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Last Live Note',
    source: 'sessionNotes.live.lastCreatedAt',
    GridComponent: GenericDateFunctionField,
    omit: true,
  },
  {
    label: 'Live Notes (deleted)',
    source: 'sessionNotes.live.deleted.count',
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Homework (active)',
    source: 'homework.active.count',
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Last Homework',
    source: 'homework.lastCreatedAt',
    GridComponent: GenericDateFunctionField,
    omit: true,
  },
  {
    label: 'Homework (shared)',
    source: 'homework.shared.count',
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Last Shared Homework',
    source: 'homework.shared.last.homeworkSharedAt',
    GridComponent: GenericDateFunctionField,
    omit: true,
  },
  {
    label: 'Homework (deleted)',
    source: 'homework.deleted.count',
    GridComponent: NumberField,
    omit: true,
  },
  {
    label: 'Last Deleted Homework',
    source: 'homework.deleted.last.deletedAt',
    GridComponent: GenericDateFunctionField,
    omit: true,
  },
  {
    label: 'Debug Mode Enabled',
    source: 'isTestTherapist',
    GridComponent: BooleanField,
    omit: true,
  },
  {
    label: 'Deleted At',
    source: 'deletedAt',
    GridComponent: GenericDateFunctionField,
    omit: true,
  },
];
